import type {
  ArtaEdition,
  ArtaOperation,
  CapabilityPackDefinition,
  ProjectEntitlement,
} from '@cubeangle/arta-common';
import {
  editionIncludesAws,
  editionIncludesAzure,
} from '@cubeangle/arta-common';

export interface EntitlementFormState {
  id: string;
  projectRef: string;
  subjectRefs: string;
  packKeys: string[];
  awsAccountIds: string;
  awsRegions: string;
  azureSubscriptionIds: string;
  azureRegions: string;
  azureResourceGroups: string;
  startsAt: string;
  expiresAt: string;
  maxActiveEnvironments: string;
  maxProviders: string;
  maxApplications: string;
  maxResources: string;
  maxEstimatedMonthlyCost: string;
  maxLeaseDays: string;
  maxIdleHours: string;
  maxEphemeralEnvironments: string;
  maxEphemeralTtlHours: string;
  maxEphemeralTotalCost: string;
  idleAction: '' | 'notify' | 'stop' | 'destroy';
  leaseExpiryAction: '' | 'notify' | 'stop' | 'destroy';
}

export const emptyEntitlementForm: EntitlementFormState = {
  id: '',
  projectRef: '',
  subjectRefs: '',
  packKeys: [],
  awsAccountIds: '',
  awsRegions: '',
  azureSubscriptionIds: '',
  azureRegions: '',
  azureResourceGroups: '',
  startsAt: '',
  expiresAt: '',
  maxActiveEnvironments: '',
  maxProviders: '',
  maxApplications: '',
  maxResources: '',
  maxEstimatedMonthlyCost: '',
  maxLeaseDays: '',
  maxIdleHours: '',
  maxEphemeralEnvironments: '',
  maxEphemeralTtlHours: '',
  maxEphemeralTotalCost: '',
  idleAction: '',
  leaseExpiryAction: '',
};

export const artaOperations: ArtaOperation[] = [
  'view',
  'provider.create',
  'environment.create',
  'application.create',
  'resource.create',
  'operate',
  'update',
  'delete',
];

function list(value: string): string[] {
  return [
    ...new Set(
      value
        .split(/[\n,]/)
        .map(item => item.trim())
        .filter(Boolean),
    ),
  ];
}

function nonNegativeNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) {
    throw new Error(`Quota value ${value} must be a non-negative number`);
  }
  return number;
}

function nonNegativeInteger(value: string): number | undefined {
  const number = nonNegativeNumber(value);
  if (number !== undefined && !Number.isInteger(number)) {
    throw new Error(`Quota value ${value} must be a whole number`);
  }
  return number;
}

function positiveInteger(value: string, label: string): number | undefined {
  const number = nonNegativeInteger(value);
  if (number !== undefined && number === 0) {
    throw new Error(`${label} must be greater than zero`);
  }
  return number;
}

export function buildEntitlementRequest(
  form: EntitlementFormState,
  packs: CapabilityPackDefinition[],
  edition: ArtaEdition,
): Omit<ProjectEntitlement, 'status'> {
  const subjects = list(form.subjectRefs);
  if (!form.id.trim() || !form.projectRef.trim() || !subjects.length) {
    throw new Error(
      'Entitlement ID, project, and at least one subject are required',
    );
  }
  const selectedPacks = form.packKeys.map(key => {
    const pack = packs.find(item => `${item.id}@${item.version}` === key);
    if (!pack) throw new Error(`Capability pack ${key} was not found`);
    const supported = new Set([
      ...pack.templates.map(template => template.operation),
      'view',
      'operate',
      'update',
      'delete',
    ] as ArtaOperation[]);
    const operations = [...supported];
    return { id: pack.id, version: pack.version, operations };
  });
  if (!selectedPacks.length)
    throw new Error('Select at least one capability pack');

  const awsEnabled = editionIncludesAws(edition);
  const azureEnabled = editionIncludesAzure(edition);
  const awsAccountIds = awsEnabled ? list(form.awsAccountIds) : [];
  const azureSubscriptionIds = azureEnabled
    ? list(form.azureSubscriptionIds)
    : [];
  if (!awsAccountIds.length && !azureSubscriptionIds.length) {
    if (edition === 'aws') throw new Error('Enter at least one AWS account');
    if (edition === 'azure')
      throw new Error('Enter at least one Azure subscription');
    throw new Error('Enter at least one AWS account or Azure subscription');
  }

  const quotas = {
    maxActiveEnvironments: nonNegativeInteger(form.maxActiveEnvironments),
    maxProviders: nonNegativeInteger(form.maxProviders),
    maxApplications: nonNegativeInteger(form.maxApplications),
    maxResources: nonNegativeInteger(form.maxResources),
    maxEstimatedMonthlyCost: nonNegativeNumber(form.maxEstimatedMonthlyCost),
    maxLeaseDays: positiveInteger(form.maxLeaseDays, 'Maximum lease days'),
    maxIdleHours: positiveInteger(form.maxIdleHours, 'Maximum idle hours'),
    maxEphemeralEnvironments: nonNegativeInteger(form.maxEphemeralEnvironments),
    maxEphemeralTtlHours: positiveInteger(
      form.maxEphemeralTtlHours,
      'Maximum ephemeral TTL',
    ),
    maxEphemeralTotalCost: nonNegativeNumber(form.maxEphemeralTotalCost),
    ...(form.idleAction ? { idleAction: form.idleAction } : {}),
    ...(form.leaseExpiryAction
      ? { leaseExpiryAction: form.leaseExpiryAction }
      : {}),
  };
  const definedQuotas = Object.fromEntries(
    Object.entries(quotas).filter(([, value]) => value !== undefined),
  );

  return {
    id: form.id.trim().toLowerCase(),
    projectRef: form.projectRef.trim(),
    subjectRefs: subjects,
    capabilityPacks: selectedPacks,
    ...(form.startsAt
      ? { startsAt: new Date(form.startsAt).toISOString() }
      : {}),
    ...(form.expiresAt
      ? { expiresAt: new Date(form.expiresAt).toISOString() }
      : {}),
    ...(awsAccountIds.length
      ? {
          aws: {
            accountIds: awsAccountIds,
            ...(list(form.awsRegions).length
              ? { regions: list(form.awsRegions) }
              : {}),
          },
        }
      : {}),
    ...(azureSubscriptionIds.length
      ? {
          azure: {
            subscriptionIds: azureSubscriptionIds,
            ...(list(form.azureRegions).length
              ? { regions: list(form.azureRegions) }
              : {}),
            ...(list(form.azureResourceGroups).length
              ? { resourceGroups: list(form.azureResourceGroups) }
              : {}),
          },
        }
      : {}),
    ...(Object.keys(definedQuotas).length ? { quotas: definedQuotas } : {}),
  };
}
